import { FC, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ListChecks, LockKey, X } from "@phosphor-icons/react";
import type { SetupTask } from "./CompleteSetupTaskList";

/**
 * Explains why installments are locked and what to do next.
 *
 * Two states, following the setup checklist's product order:
 *  - the first task is still outstanding: name it and start it directly;
 *  - the first task is done but others remain: say how many and open the
 *    checklist.
 * Wallet payment never needs any of this, so it is always offered as the way
 * to keep going right now.
 */
const BnplSetupGateDialog: FC<{
  isOpen: boolean;
  onClose: () => void;
  firstTaskPending: SetupTask | null;
  pendingTasks: SetupTask[];
  onStartTask: (task: SetupTask) => void;
  onViewAllSteps: () => void;
  onPayWithWallet?: () => void;
}> = ({
  isOpen,
  onClose,
  firstTaskPending,
  pendingTasks,
  onStartTask,
  onViewAllSteps,
  onPayWithWallet,
}) => {
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    primaryRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const remaining = pendingTasks.length;
  const stepsWord = remaining === 1 ? "step" : "steps";

  const title = firstTaskPending
    ? "Set up your account to pay in installments"
    : `Almost there: ${remaining} ${stepsWord} left`;

  return (
    <AnimatePresence>
      {isOpen && remaining > 0 && (
        <motion.div
          className="fixed inset-0 z-[99997] bg-black/30 backdrop-blur-sm flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="bnpl-gate-title"
            aria-describedby="bnpl-gate-body"
            initial={{ scale: 0.95, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className="bg-white rounded-2xl shadow-lg w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition"
            >
              <X size={18} weight="bold" />
            </button>

            <div className="flex flex-col gap-4">
              <span className="flex items-center justify-center size-12 rounded-full bg-visaro-orange/10 text-visaro-orange">
                {firstTaskPending ? (
                  <LockKey size={26} weight="fill" />
                ) : (
                  <ListChecks size={26} weight="bold" />
                )}
              </span>

              <div className="flex flex-col gap-2 pr-6">
                <h2
                  id="bnpl-gate-title"
                  className="text-xl font-semibold text-deepBlue"
                >
                  {title}
                </h2>
                <p id="bnpl-gate-body" className="text-gray-600 text-sm">
                  {firstTaskPending ? (
                    <>
                      Paying in installments needs a fully set-up account. Start
                      with the step below. We’ll bring you back here when it’s
                      done.
                    </>
                  ) : (
                    <>
                      Finish setting up your account to pay in installments.
                      Your flight stays selected while you complete each step.
                    </>
                  )}
                </p>
              </div>

              {firstTaskPending ? (
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="font-medium text-deepBlue">
                    {firstTaskPending.label}
                  </div>
                  <div className="text-sm text-gray-500 mt-0.5">
                    {firstTaskPending.description}
                  </div>
                </div>
              ) : (
                <ul className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {pendingTasks.slice(0, 3).map((task) => (
                    <li key={task.label} className="px-4 py-3 text-sm">
                      <span className="font-medium text-deepBlue">
                        {task.label}
                      </span>
                    </li>
                  ))}
                  {remaining > 3 && (
                    <li className="px-4 py-3 text-sm text-gray-500">
                      and {remaining - 3} more
                    </li>
                  )}
                </ul>
              )}

              <div className="flex flex-col gap-2 pt-1">
                <button
                  ref={primaryRef}
                  type="button"
                  onClick={() =>
                    firstTaskPending
                      ? onStartTask(firstTaskPending)
                      : onViewAllSteps()
                  }
                  className="p-4 px-5 w-full rounded-lg font-bold bg-deepBlue text-white text-sm hover:bg-opacity-90 transition"
                >
                  {firstTaskPending ? firstTaskPending.label : "Continue setup"}
                </button>
                {firstTaskPending && remaining > 1 && (
                  <button
                    type="button"
                    onClick={onViewAllSteps}
                    className="p-3 w-full rounded-lg font-medium text-deepBlue text-sm border border-gray-300 hover:bg-gray-50 transition"
                  >
                    See all {remaining} steps
                  </button>
                )}
                {onPayWithWallet && (
                  <button
                    type="button"
                    onClick={onPayWithWallet}
                    className="p-3 w-full rounded-lg font-medium text-visaro-orange text-sm hover:bg-visaro-orange/5 transition"
                  >
                    Pay now with wallet instead
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BnplSetupGateDialog;
